import React, { useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Box, Button, Checkbox, IconButton, Tooltip } from "@mui/material";

import { ExportCafsOptions } from "../../../api/types/export";
import { Rubber } from "../../../components/icons/Rubber";
import { StyledTabItem } from "../../../components/StyledTabItem";
import { StyledTabs } from "../../../components/StyledTabs";
import { useExportAllCafs } from "../api/useExportAllCafs";
import { useExportAllCalibrations } from "../api/useExportAllCalibrations";
import { useExportSelectedCafs } from "../api/useExportSelectedCafs";
import { useExportSelectedCalibrations } from "../api/useExportSelectedCalibrations";
import { DashboardContext } from "../DashboardContext";

export function CafsExport() {
  const {
    selectedPlants,
    selectedInputs,
    selectedDataset,
    handleDeselectAllCafs,
  } = useContext(DashboardContext);

  const selectedNodes = [...selectedPlants, ...selectedInputs];

  const { exportAllCafs } = useExportAllCafs();
  const { exportSelectedCafs } = useExportSelectedCafs();
  const { exportAllCalibrations } = useExportAllCalibrations();
  const { exportSelectedCalibrations } = useExportSelectedCalibrations();

  const { formatMessage } = useIntl();

  const [currentTab, setCurrentTab] = useState(0);
  const [exportOptions, setExportOptions] = useState<ExportCafsOptions>({
    IncludeReferences: false,
    IncludeDescriptions: false,
    IncludeUnits: false,
  });

  const handleExportOptionChange =
    (option: keyof ExportCafsOptions) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setExportOptions(current => ({
        ...current,
        [option]: event.target.checked,
      }));
    };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return selectedDataset ? (
    <div className="flex flex-col gap-4" data-testid="cafs-export">
      <StyledTabs value={currentTab} onChange={handleTabChange}>
        <StyledTabItem
          index={0}
          selected={currentTab === 0}
          label="CAFS Data"
        />
        <StyledTabItem
          index={1}
          selected={currentTab === 1}
          label="Calibration Results"
        />
      </StyledTabs>

      {currentTab === 0 && (
        <Box
          className="flex flex-col gap-4"
          sx={{ padding: "0.5rem", minHeight: "150px" }}>
          <div className="flex gap-4 items-center">
            <Tooltip
              title={formatMessage({
                id: "TOOLTIP.EXPORT_SELECTED_CAFS",
                defaultMessage: "Export data of selected CAFS of the dataset",
                description: "Tooltip message on export selected CAFS button",
              })}>
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!selectedNodes.length}
                  onClick={() => exportSelectedCafs(exportOptions)}>
                  <FormattedMessage
                    id="EXPORT_CAFS.SELECTED"
                    defaultMessage="Export selected ({count})"
                    values={{ count: selectedNodes.length }}
                  />
                </Button>
              </div>
            </Tooltip>
            <Tooltip
              title={formatMessage({
                id: "TOOLTIP.EXPORT_ALL_CAFS",
                defaultMessage: "Export data of all CAFS in the dataset",
                description: "Tooltip message on export all CAFS button",
              })}>
              <div>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => exportAllCafs(exportOptions)}>
                  <FormattedMessage
                    id="EXPORT_CAFS.ALL"
                    defaultMessage="Export all"
                    description="Export all cavs buttin"
                  />
                </Button>
              </div>
            </Tooltip>
            <Tooltip
              title={formatMessage({
                id: "TOOLTIP.CLEAR_SELECTED_DATASETS",
                defaultMessage: "Clear selected datasets",
                description: "Clear selected datasets tooltip text",
              })}>
              <div>
                <IconButton
                  className="hover:text-red-500"
                  disabled={!selectedNodes.length}
                  onClick={handleDeselectAllCafs}>
                  <Rubber />
                </IconButton>
              </div>
            </Tooltip>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <FormattedMessage
              id="EXPORT_CAFS.INCLUDE_REFERENCES"
              defaultMessage="Include references"
              description="Checkbox label for including references in export"
            />
            <Checkbox
              checked={!!exportOptions.IncludeReferences}
              onChange={handleExportOptionChange("IncludeReferences")}
            />
          </div>
          <div className="flex items-center gap-2 justify-end">
            <FormattedMessage
              id="EXPORT_CAFS.INCLUDE_DESCRIPTIONS"
              defaultMessage="Include descriptions"
              description="Checkbox label for including variable descriptions in export"
            />
            <Checkbox
              checked={!!exportOptions.IncludeDescriptions}
              onChange={handleExportOptionChange("IncludeDescriptions")}
            />
          </div>
          <div className="flex items-center gap-2 justify-end">
            <FormattedMessage
              id="EXPORT_CAFS.INCLUDE_UNITS"
              defaultMessage="Include units"
              description="Checkbox label for including variable units in export"
            />
            <Checkbox
              checked={!!exportOptions.IncludeUnits}
              onChange={handleExportOptionChange("IncludeUnits")}
            />
          </div>
        </Box>
      )}

      {currentTab === 1 && (
        <Box
          className="flex flex-col gap-4"
          sx={{ padding: "0.5rem", minHeight: "150px" }}>
          <div className="flex gap-4 items-center">
            <Tooltip
              title={formatMessage({
                id: "TOOLTIP.EXPORT_SELECTED_CALIBRATIONS",
                defaultMessage:
                  "Export calibration results of selected CAFS of the dataset",
                description:
                  "Tooltip message on export selected calibrations button",
              })}>
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!selectedNodes.length}
                  onClick={() => exportSelectedCalibrations()}>
                  <FormattedMessage
                    id="EXPORT_CALIBRATIONS.SELECTED"
                    defaultMessage="Export selected ({count})"
                    values={{ count: selectedNodes.length }}
                  />
                </Button>
              </div>
            </Tooltip>
            <Tooltip
              title={formatMessage({
                id: "TOOLTIP.EXPORT_ALL_CALIBRATIONS",
                defaultMessage:
                  "Export calibration results of all CAFS in the dataset",
                description:
                  "Tooltip message on export all calibrations button",
              })}>
              <div>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => exportAllCalibrations()}>
                  <FormattedMessage
                    id="EXPORT_CALIBRATIONS.ALL"
                    defaultMessage="Export all"
                    description="Export all calibrations button"
                  />
                </Button>
              </div>
            </Tooltip>
            <Tooltip
              title={formatMessage({
                id: "TOOLTIP.CLEAR_SELECTED_DATASETS",
                defaultMessage: "Clear selected datasets",
                description: "Clear selected datasets tooltip text",
              })}>
              <div>
                <IconButton
                  className="hover:text-red-500"
                  disabled={!selectedNodes.length}
                  onClick={handleDeselectAllCafs}>
                  <Rubber />
                </IconButton>
              </div>
            </Tooltip>
          </div>
        </Box>
      )}
    </div>
  ) : null;
}
